import {
  COPPEL_COMISION_MINIMA,
  COPPEL_DESTINOS_ADQUISICION,
  COPPEL_DESTINOS_MEJORA,
  COPPEL_ESQUEMA_CRECIENTE,
  COPPEL_ESQUEMA_FIJO,
  COPPEL_GASTOS_ENUM,
  COPPEL_GASTOS_POR_ESTADO,
  COPPEL_PLAZOS_ANIOS,
  COPPEL_PLAZOS_POR_ESQUEMA,
  COPPEL_PROGRAMA_APOYO_INFONAVIT,
  COPPEL_PROGRAMA_COFINAVIT,
  COPPEL_PROGRAMA_TRADICIONAL,
  COPPEL_TASA_DEFAULT,
} from './constants'
import { buildAnswersContextoCoppel, resolveTasasCoppelParaCombo } from './tasas'
import { resolveOpcionesCampo } from './template'

export function parseNumero(valor) {
  if (valor == null || valor === '') return 0
  if (typeof valor === 'number') return valor
  return parseFloat(String(valor).replace(/[$,]/g, '')) || 0
}

export function formatFechaCoppel(moment) {
  return moment().format('DD/MM/YYYY')
}

export function destinoSoportaCoppel(destinoId) {
  const id = parseInt(destinoId, 10)
  return COPPEL_DESTINOS_ADQUISICION.includes(id) || COPPEL_DESTINOS_MEJORA.includes(id)
}

export function esDestinoAdquisicion(destinoId) {
  return COPPEL_DESTINOS_ADQUISICION.includes(parseInt(destinoId, 10))
}

export function esDestinoMejora(destinoId) {
  return COPPEL_DESTINOS_MEJORA.includes(parseInt(destinoId, 10))
}

export function debeCotizarEsquema(destinoId, esquemaId) {
  const esquema = parseInt(esquemaId, 10)
  return esquema === COPPEL_ESQUEMA_FIJO || esquema === COPPEL_ESQUEMA_CRECIENTE
}

export function mapEsquemaCoppel(esquemaId) {
  return parseInt(esquemaId, 10) === COPPEL_ESQUEMA_FIJO ? 'Pagos fijos' : 'Pagos ligeros'
}

export function labelEsquemaCoppel(esquemaId) {
  return parseInt(esquemaId, 10) === COPPEL_ESQUEMA_FIJO ? 'Pago fijo' : 'Pago creciente'
}

/** Plazos Abacus permitidos (plantilla `conditional_options` / `inputs`, con fallback local). */
export function plazosCoppelParaEsquema(esquemaId, plazosAnios, template, destinoId) {
  const esquema = parseInt(esquemaId, 10)
  let permitidos = null

  if (template) {
    const ctx = buildAnswersContextoCoppel(destinoId, esquemaId, mapProductoFinanciamiento, mapEsquemaCoppel)
    permitidos = resolveOpcionesCampo(template, 'plazo_en_anos', ctx)
  }

  if (!permitidos || !permitidos.length) {
    permitidos = COPPEL_PLAZOS_POR_ESQUEMA[esquema] || COPPEL_PLAZOS_ANIOS
  }

  const setPerm = new Set(permitidos.map((p) => parseInt(p, 10)))
  return (plazosAnios || []).filter((p) => setPerm.has(parseInt(p, 10)))
}

/** Valores enum Abacus SOC (con acentos) */
export function mapProgramaCoppel(programaId) {
  const p = parseInt(programaId, 10)
  if (p === COPPEL_PROGRAMA_APOYO_INFONAVIT) return 'Apoyo Infonavit'
  if (p === COPPEL_PROGRAMA_COFINAVIT) return 'Cofinavit'
  return 'Crédito tradicional'
}

export function mapProductoFinanciamiento(destinoId) {
  return esDestinoAdquisicion(destinoId) ? 'Adquisición de vivienda' : 'Mejora de hipoteca'
}

export function resolveEstadoCoppel(estados, estadoId) {
  if (!estados || !estadoId) return ''
  const estado = estados.find((e) => parseInt(e.Id, 10) === parseInt(estadoId, 10))
  return estado ? String(estado.Nombre).trim() : ''
}

export function resolvePorcentajeGastosNotariales(estadoNombre, pctUsuario) {
  if (estadoNombre && COPPEL_GASTOS_POR_ESTADO[estadoNombre] != null) {
    return COPPEL_GASTOS_POR_ESTADO[estadoNombre]
  }
  const pct = parseNumero(pctUsuario)
  const match = COPPEL_GASTOS_ENUM.find((v) => Math.abs(v - pct) < 0.0001)
  if (match != null) return match
  return COPPEL_GASTOS_ENUM.includes(pct) ? pct : 0.08
}

export function resolveComisionApertura(pctUsuario) {
  const pct = parseNumero(pctUsuario)
  if (pct > 0) return pct
  return COPPEL_COMISION_MINIMA
}

export function filtrarPlazosCoppel(plazos) {
  const out = []
  const vistos = new Set()
  ;(plazos || []).forEach((p) => {
    const anios = parseInt(typeof p === 'object' ? p.value || p : p, 10)
    if (COPPEL_PLAZOS_ANIOS.includes(anios) && !vistos.has(anios)) {
      vistos.add(anios)
      out.push(anios)
    }
  })
  return out
}

/** No usar `if (entry.value)` — falla cuando el esquema es Pago creciente (value === 1). */
export function normalizarEsquemaIds(esquemasSeleccionados) {
  const ids = []
  ;(esquemasSeleccionados || []).forEach((entry) => {
    let id = NaN
    if (entry != null && typeof entry === 'object' && entry.value !== undefined && entry.value !== null && entry.value !== '') {
      id = parseInt(entry.value, 10)
    } else if (entry !== undefined && entry !== null && entry !== '') {
      id = parseInt(entry, 10)
    }
    if (!Number.isNaN(id)) ids.push(id)
  })
  return [...new Set(ids)]
}

export function normalizarPlazosDesdeCaso(plazos) {
  const raw = []
  ;(plazos || []).forEach((p) => {
    if (p == null || p === '') return
    if (typeof p === 'object' && p.value !== undefined && p.value !== null && p.value !== '') {
      raw.push(parseInt(p.value, 10))
    } else {
      const n = parseInt(p, 10)
      if (!Number.isNaN(n)) raw.push(n)
    }
  })
  return raw
}

export function buildCoppelAnswers(vm, { destinoId, esquemaId, plazoAnios, tasaDeInteres }) {
  const valorInmueble = parseNumero(vm.ValorViendaCofi || vm.caso.valorInmueble)
  const montoCredito = parseNumero(vm.caso.montoSolicitado)
  const enganche = Math.max(0, valorInmueble - montoCredito)
  const gastosPctForm = parseNumero(vm.caso.gastosNotarial) / 100
  const programa = parseInt(vm.caso.programa, 10)
  const salario = parseNumero(vm.salarioIntegrado || vm.caso.salarioIntegrado)
  const estadoNombre = resolveEstadoCoppel(vm.estados, vm.caso.estado)

  const answers = {
    producto_de_financiamiento: mapProductoFinanciamiento(destinoId),
    esquema_de_pago: mapEsquemaCoppel(esquemaId),
    tipo_de_credito: mapProgramaCoppel(programa),
    tipo_de_financiamiento: 'Mercado abierto',
    fecha: formatFechaCoppel(vm.moment),
    valor_del_inmueble: valorInmueble,
    plazo_en_anos: plazoAnios,
    tasa_de_interes: tasaDeInteres != null ? tasaDeInteres : COPPEL_TASA_DEFAULT,
    porcentaje_gastos_notariales: resolvePorcentajeGastosNotariales(estadoNombre, gastosPctForm),
    porcentaje_comision_por_apertura: resolveComisionApertura(vm.caso.comisionApertura),
    financiamiento_de_comision_por_apertura: 'No',
    estado_en_donde_se_encuentra_la_propiedad: estadoNombre,
  }

  if (esDestinoAdquisicion(destinoId)) {
    answers.enganche = enganche
    const pctEnganche = valorInmueble > 0 ? (enganche / valorInmueble) * 100 : 0
    answers.porcentaje_de_enganche = Math.round(pctEnganche * 100) / 100
  } else {
    answers.saldo_pendiente = montoCredito
    answers.mensualidad = 0
    const pctEnganche = valorInmueble > 0 ? ((valorInmueble - montoCredito) / valorInmueble) * 100 : 0
    answers.porcentaje_de_enganche = Math.round(pctEnganche * 100) / 100
  }

  if (programa === COPPEL_PROGRAMA_APOYO_INFONAVIT) {
    answers.monto_de_credito_infonavit = parseNumero(vm.montoCreditoCofi || (vm.caso && vm.caso.creditoInf))
    answers.saldo_en_subcuenta = parseNumero(vm.ssvCofi || (vm.caso && vm.caso.ssv))
    answers.descuento_mensual_infonavit = salario * 0.05
    answers.aportacion_sueldo = salario
  } else if (programa === COPPEL_PROGRAMA_COFINAVIT) {
    answers.monto_de_credito_infonavit = parseNumero(vm.montoCreditoCofi || (vm.caso && vm.caso.creditoInf))
    answers.saldo_en_subcuenta = parseNumero(vm.ssvCofi || (vm.caso && vm.caso.ssv))
    answers.descuento_mensual_infonavit = salario * 0.05
  }

  return answers
}

export function listarCombinacionesCoppel(vm, esquemasIds, plazosOverride, template) {
  const destinoId = parseInt(vm.destino || (vm.caso && vm.caso.destino), 10)
  if (!destinoSoportaCoppel(destinoId)) return []

  const plazos = filtrarPlazosCoppel(plazosOverride || vm.caso.plazos)
  const esquemasNorm = normalizarEsquemaIds(esquemasIds)
  const combinaciones = []

  esquemasNorm.forEach((esquemaId) => {
    if (!debeCotizarEsquema(destinoId, esquemaId)) return
    const plazosEsquema = plazosCoppelParaEsquema(esquemaId, plazos, template, destinoId)
    const answersContext = buildAnswersContextoCoppel(destinoId, esquemaId, mapProductoFinanciamiento, mapEsquemaCoppel)
    const tasas = resolveTasasCoppelParaCombo(vm, template, answersContext)
    plazosEsquema.forEach((plazoAnios) => {
      tasas.forEach((tasaDeInteres) => {
        combinaciones.push({ destinoId, esquemaId, plazoAnios, tasaDeInteres })
      })
    })
  })

  return combinaciones
}
